import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api from "@/admin/api";
import { Logo } from "@/components/comiq/Logo";
import { AnswerInput } from "@/components/comiq/AnswerInput";
import { SectionHeader, BlockPrompt } from "@/components/comiq/SectionHeader";
import { COLORS } from "@/comiq/tokens";
import { ArrowLeft, ArrowRight, Ban, Eye } from "lucide-react";

const READ = "mx-auto max-w-[680px] px-5";

// Read-only respondent-faithful preview of ONE instrument version (draft or published).
// Uses the exact same AnswerInput component as /s/:token. Answers live in local state
// only — no submit, no autosave, no invitation/token/response is ever created.
export default function InstrumentPreview() {
  const { versionId } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState({});

  useEffect(() => {
    setIdx(0);
    setAnswers({});
    setError(false);
    api.get(`/admin/versions/${versionId}`).then((r) => setData(r.data)).catch(() => setError(true));
  }, [versionId]);

  const flat = useMemo(() => {
    if (!data) return [];
    const scales = {};
    (data.scales || []).forEach((s) => { scales[s.scale_id] = s; });
    const sections = [...(data.sections || [])].sort((a, b) => a.display_order - b.display_order);
    const out = [];
    sections.forEach((sec) => {
      (data.items || [])
        .filter((i) => i.section_id === sec.section_id)
        .sort((a, b) => a.display_order - b.display_order)
        .forEach((it) => {
          const sc = it.scale_id ? scales[it.scale_id] : null;
          out.push({
            item_id: it.item_id,
            stable_item_code: it.stable_item_code,
            wording: it.wording,
            item_type: it.item_type,
            allow_unsure: it.allow_unsure,
            group_prompt: it.group_prompt,
            section_id: sec.section_id,
            section_code: sec.code,
            section_label: sec.label || null,
            section_title: sec.title || null,
            section_description: sec.description || null,
            section_instruction: sec.instruction || null,
            display_label: it.display_label || null,
            unsure_label: it.unsure_label || null,
            scale: sc ? { min: sc.min, max: sc.max, anchors: sc.anchors } : null,
            options: (it.options || []).map((o) => ({ code: o.code, label: o.label })),
          });
        });
    });
    return out;
  }, [data]);

  if (error) {
    return (
      <div className="min-h-screen" style={{ background: COLORS.paper }}>
        <div className={`${READ} py-14`} data-testid="preview-error">
          <p className="text-sm" style={{ color: COLORS.textSoft }}>Versi instrumen tidak dapat dimuat.</p>
          <Link to="/admin/instruments" className="mt-4 inline-flex items-center gap-1.5 text-sm" style={{ color: COLORS.orangeInk }} data-testid="preview-error-back-btn"><ArrowLeft size={14} /> Kembali ke instrumen</Link>
        </div>
      </div>
    );
  }
  if (!data) return <div className="p-6 text-sm" style={{ color: COLORS.textSoft }} data-testid="preview-loading">Memuat pratinjau…</div>;

  const it = flat[idx];
  const val = answers[it.item_id];
  const prev = idx > 0 ? flat[idx - 1] : null;
  const showSection = !prev || prev.section_id !== it.section_id;
  const showPrompt = Boolean((it.group_prompt || "").trim()) && (!prev || (prev.group_prompt || "").trim() !== (it.group_prompt || "").trim());

  return (
    <div className="min-h-screen" style={{ background: COLORS.paper }} data-testid="respondent-preview">
      <header className="border-b" style={{ borderColor: COLORS.line, background: COLORS.surfaceAlt }}>
        <div className="mx-auto flex max-w-[680px] flex-wrap items-center justify-between gap-2 px-5 py-4">
          <Logo />
          <div className="flex items-center gap-2">
            <button onClick={() => window.open(`/admin/instruments/${versionId}/respondent-view`, "_blank", "noopener")} className="btn-comiq btn-line !px-3 !py-1 text-xs" data-testid="preview-open-as-respondent-btn">Buka sebagai Responden</button>
            <span className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold" style={{ background: "#FEF3C7", color: "#B45309" }} data-testid="preview-mode-badge">
              <Eye size={12} /> Mode pratinjau — jawaban tidak disimpan
            </span>
          </div>
        </div>
      </header>

      <div className={`${READ} pt-6`}>
        <Link to="/admin/instruments" className="inline-flex items-center gap-1.5 text-sm" style={{ color: COLORS.textSoft }} data-testid="preview-back-btn">
          <ArrowLeft size={14} /> Kembali ke instrumen
        </Link>
        <div className="mt-2 text-xs" style={{ color: COLORS.textSoft }} data-testid="preview-progress">
          Item {idx + 1} dari {flat.length} · v{data.version.version_no}
        </div>
      </div>

      <main className={`${READ} py-8`} data-testid="preview-question-screen">
        {showSection && (
          <SectionHeader
            label={it.section_label}
            title={it.section_title}
            description={it.section_description}
            instruction={it.section_instruction}
            sectionCode={it.section_code}
            testPrefix="preview-"
          />
        )}
        {showPrompt && <BlockPrompt prompt={it.group_prompt} testPrefix="preview-" />}
        <h2 className="mt-3 text-2xl font-semibold" style={{ color: COLORS.ink, lineHeight: 1.4 }} data-testid="preview-question-wording">{it.wording}</h2>
        {it.display_label && (
          <div className="mt-2" data-testid="preview-display-label">
            <span className="rounded-full px-2 py-0.5 text-[0.65rem] font-semibold uppercase tracking-wide" style={{ background: "#F1EDE4", color: COLORS.textSoft }}>{it.display_label}</span>
          </div>
        )}

        <div className="mt-7">
          <AnswerInput
            item={it}
            value={val}
            onAnswer={(v) => setAnswers((a) => ({ ...a, [it.item_id]: v }))}
            testPrefix={`preview-${it.stable_item_code}-`}
          />
        </div>

        <div className="mt-9 flex items-center justify-between">
          <button onClick={() => setIdx((i) => Math.max(0, i - 1))} disabled={idx === 0} className="btn-comiq btn-line disabled:opacity-40" data-testid="preview-prev-btn">
            <ArrowLeft size={16} /> Kembali
          </button>
          {idx < flat.length - 1 ? (
            <button onClick={() => setIdx((i) => Math.min(flat.length - 1, i + 1))} className="btn-comiq btn-accent" data-testid="preview-next-btn">
              Lanjut <ArrowRight size={16} />
            </button>
          ) : (
            <button disabled className="btn-comiq btn-accent cursor-not-allowed opacity-40" title="Pengiriman dinonaktifkan pada mode pratinjau" data-testid="preview-submit-disabled">
              <Ban size={15} /> Kirim jawaban (nonaktif)
            </button>
          )}
        </div>
      </main>
    </div>
  );
}
